// Templates 10–13 · Vendor · bar contact
// How Snafu hears about you.

import {
  renderShell,
  renderStatusDot,
  renderHeading,
  renderParagraph,
  renderInfoCard,
  renderButtonRow,
  renderStatRow,
  renderFooter,
  tokens,
  cleanSubject,
} from './kit.ts';
import { formatLongDate, formatTimeRange, formatCurrency, escapeHtml, formatShortDate } from './format.ts';
import type {
  RenderedEmail,
  VendorBookingNotificationData,
  VendorHeadcountLockData,
  VendorBookingCancelledData,
  VendorWeeklySummaryData,
} from './types.ts';

const fontStack = `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
const monoStack = `ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, monospace`;
const displayStack = `'Bricolage Grotesque', 'DM Sans', sans-serif`;

// ─── 10 · BOOKING NOTIFICATION (VENDOR) ───────────────────────────────────
export function renderVendorBookingNotification(d: VendorBookingNotificationData): RenderedEmail {
  const dateLong = formatLongDate(d.date);
  const dayName = dateLong.split(',')[0];
  const subject = cleanSubject(
    `New booking: ${d.organizerOrgName ?? d.organizerFirstName}, ${dateLong} · ${d.headcount} ppl`,
  );
  const preheader = `${formatCurrency(d.pricePerHeadCents)}/head · ${escapeHtml(d.offerTitle)} · ${d.capacityMin ?? 4}-person min`;

  const body = `
    ${renderStatusDot('New booking', 'confirmed')}
    ${renderHeading(`${d.organizerOrgName ?? d.organizerFirstName} just booked`, dayName, `at ${formatTimeRange(d.startTime, d.endTime).split(' ')[0]}.`)}
    ${renderParagraph(
      `Starting headcount is ${d.headcount}, with capacity up to ${d.capacityMax ?? d.headcount}. We'll lock the final number at 3 PM ${escapeHtml(dayName)} and settle with you within 48 hours of the event.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Date', value: `${escapeHtml(dateLong)} · ${escapeHtml(formatTimeRange(d.startTime, d.endTime))}` },
        { label: 'Package', value: `${escapeHtml(d.offerTitle)} · ${formatCurrency(d.pricePerHeadCents)} / head` },
        { label: "RSVP'd", value: `${d.headcount} of ${d.capacityMax ?? d.headcount} (will move toward 3 PM ${escapeHtml(dayName.slice(0, 3))})` },
        { label: 'Organizer', value: `${escapeHtml(d.organizerFullName)} · ${escapeHtml(d.organizerEmail)}` },
        ...(d.dietary ? [{ label: 'Dietary', value: escapeHtml(d.dietary) }] : []),
        { label: 'Door name', value: `"${escapeHtml(d.doorName)}"` },
      ],
      { tone: 'amber' },
    )}
    ${renderButtonRow([
      { label: 'Open in dashboard →', href: d.dashboardUrl, style: 'primary' },
      { label: 'Reply to ' + d.organizerFirstName, href: d.replyUrl, style: 'ghost' },
    ])}
  `;

  const footer = renderFooter({ tagline: `Sent to vendor primary contact`, pill: 'Booking' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: `For ${d.vendorName}`, body, footer }),
  };
}

// ─── 11 · 3 PM HEADCOUNT LOCK + PAYMENT ───────────────────────────────────
export function renderVendorHeadcountLock(d: VendorHeadcountLockData): RenderedEmail {
  const subject = cleanSubject(
    `Tonight's count is locked: ${d.headcount} ppl, ${d.organizerOrgName ?? d.organizerFirstName}`,
  );
  const preheader = `${formatCurrency(d.payoutCents)} paid out · settles to your account ${d.payoutDay}.`;
  const doorTime = formatTimeRange(d.startTime, d.endTime).split(' ')[0];

  const body = `
    ${renderStatusDot('Headcount locked', 'confirmed')}
    ${renderHeading(`Pour for`, String(d.headcount), 'tonight.')}
    ${renderParagraph(
      `We've just charged ${escapeHtml(d.organizerOrgName ?? d.organizerFirstName)} and locked the final headcount. Settles to your connected account by end of day ${escapeHtml(d.payoutDay)}.`,
    )}
    ${renderStatRow([
      { label: 'Headcount', value: String(d.headcount), sub: `${d.headcountDelta >= 0 ? '+' : ''}${d.headcountDelta} since this morning` },
      { label: 'Payout', value: formatCurrency(d.payoutCents), sub: `Settles ${d.payoutDay}` },
      { label: 'Door at', value: doorTime, sub: `"${d.doorName}"` },
    ])}
    ${d.dietary ? renderParagraph(`Dietary refresh: ${escapeHtml(d.dietary)}.`) : ''}
    ${renderButtonRow([{ label: "View tonight's run-sheet →", href: d.runSheetUrl, style: 'primary' }])}
  `;

  const footer = renderFooter({ tagline: `Final headcount · won't change`, pill: 'Day-of' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: `Day-of lock · 3 PM`, body, footer }),
  };
}

// ─── 12 · BOOKING CANCELLED (VENDOR) ──────────────────────────────────────
export function renderVendorBookingCancelled(d: VendorBookingCancelledData): RenderedEmail {
  const dateLong = formatLongDate(d.date);
  const dayName = dateLong.split(',')[0];
  const subject = cleanSubject(`${d.organizerOrgName ?? d.organizerFirstName} cancelled ${dayName}'s round`);
  const preheader = `${formatTimeRange(d.startTime, d.endTime)} slot is back on the calendar.`;

  const body = `
    ${renderStatusDot('Cancelled', 'cancelled')}
    ${renderHeading(`${dayName}'s`, 'off.')}
    ${renderParagraph(
      `${escapeHtml(d.organizerOrgName ?? d.organizerFirstName)}'s booking ${escapeHtml(d.reason.toLowerCase().includes('auto') ? 'auto-cancelled' : 'was cancelled')} — ${escapeHtml(d.reason)}. No payout this round; we've already refunded their card.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Was', value: `${escapeHtml(dateLong)} · ${escapeHtml(formatTimeRange(d.startTime, d.endTime))}` },
        { label: 'Slot status', value: `Open · back on your calendar` },
        { label: 'Reason', value: escapeHtml(d.reason) },
      ],
      { tone: 'pink' },
    )}
    ${renderButtonRow([{ label: 'View open slots', href: d.openSlotsUrl, style: 'primary' }])}
  `;

  const footer = renderFooter({ tagline: `Avg vendor takes 1.2 cancellations / mo`, pill: 'Cancellation' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: `For ${d.vendorName}`, body, footer }),
  };
}

// ─── 13 · WEEKLY PERFORMANCE SUMMARY ──────────────────────────────────────
export function renderVendorWeeklySummary(d: VendorWeeklySummaryData): RenderedEmail {
  const subject = cleanSubject(
    `${d.vendorName} · last week: ${d.weekBookings} round${d.weekBookings === 1 ? '' : 's'} · ${formatCurrency(d.weekRevenueCents)}`,
  );
  const preheader = `${d.weekChangePct >= 0 ? 'Up' : 'Down'} ${Math.abs(d.weekChangePct)}% vs. the week prior. Next 7 days at a glance.`;

  const upcomingRows = d.upcoming
    .map(
      (u) => `<tr>
        <td style="padding:14px 0;border-bottom:1px solid ${tokens.line};width:24%;">
          <div style="font-family:${monoStack};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${tokens.inkSoft};">${escapeHtml(u.date)}</div>
        </td>
        <td style="padding:14px 0;border-bottom:1px solid ${tokens.line};">
          <div style="font-family:${fontStack};font-size:15px;color:${tokens.ink};">${escapeHtml(u.teamName)} · ${u.headcount} ppl · ${escapeHtml(u.packageTitle)}</div>
        </td>
        <td align="right" style="padding:14px 0;border-bottom:1px solid ${tokens.line};">
          <div style="font-family:${fontStack};font-weight:600;font-size:15px;color:${tokens.ink};">${formatCurrency(u.revenueCents)}</div>
        </td>
      </tr>`,
    )
    .join('');

  const upcomingTable = d.upcoming.length
    ? `<div style="text-align:center;font-family:${monoStack};font-size:10px;letter-spacing:0.16em;text-transform:uppercase;color:${tokens.inkSoft};margin:24px 0 4px;">Next 7 days</div>
       <table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;margin:0 0 24px;">${upcomingRows}</table>`
    : '';

  const body = `
    ${renderStatusDot('Monday recap', 'action')}
    ${renderHeading('Last week ·', `${d.weekBookings} rounds, ${formatCurrency(d.weekRevenueCents)}.`)}
    ${renderParagraph(
      `${d.weekChangePct >= 0 ? 'Up' : 'Down'} ${Math.abs(d.weekChangePct)}% from the week prior.${d.ratingDelta !== 0 ? ` Your average is now ${d.avgRating.toFixed(1)}.` : ''}`,
    )}
    ${renderStatRow([
      { label: 'Bookings', value: String(d.weekBookings), sub: `${d.weekBookingsDelta >= 0 ? '+' : ''}${d.weekBookingsDelta} vs. last wk` },
      { label: 'Revenue', value: formatCurrency(d.weekRevenueCents), sub: `${d.weekChangePct >= 0 ? '+' : ''}${d.weekChangePct}%` },
      { label: 'Avg rating', value: d.avgRating.toFixed(1), sub: `${d.ratingDelta >= 0 ? '+' : ''}${d.ratingDelta.toFixed(1)}` },
    ])}
    ${upcomingTable}
    ${renderButtonRow([
      { label: 'Open dashboard →', href: d.dashboardUrl, style: 'primary' },
      { label: 'Update offer', href: d.updateOfferUrl, style: 'ghost' },
    ])}
  `;

  const tagline = `Weekly · turn off in <a href="${escapeHtml(d.settingsUrl)}" style="color:${tokens.inkSoft};text-decoration:underline;">settings</a>`;
  const footerHtml = renderFooter({ tagline: '__TAGLINE__', pill: 'Weekly' }).replace('__TAGLINE__', tagline);

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: `Weekly · ${d.vendorName}`, body, footer: footerHtml }),
  };
}
